// Module permissions: what a role may do in each part of the app, as
// "module.action" strings such as "loto.read" or "infrared.reports".
//
// The API decides. It sends the signed-in user's permissions with /me (already
// worked out from all their roles, implied reads included), and every request
// is checked again on the server, so these checks only decide what the app
// shows. This file mirrors the API's catalog (bowvue-graphql auth/permissions.go)
// so a misspelt permission in the app is caught by a test rather than quietly
// hiding something forever. Change both together.

import { hasAnyRole } from '@utils/roles'

// Every module and its actions, read first. Same order as the API.
export const PERMISSION_CATALOG = [
  { module: 'arcflash', actions: ['read', 'write', 'import', 'maintenance'] },
  {
    module: 'infrared',
    actions: ['read', 'write', 'reports', 'offline', 'codes', 'legacy'],
  },
  {
    module: 'loto',
    actions: [
      'read',
      'write',
      'finalize',
      'edit_final',
      'sign_normal',
      'sign_complex',
      'manage_lockouts',
      'archive_import',
      'offline',
    ],
  },
  { module: 'documents', actions: ['read', 'write', 'archive', 'project'] },
  { module: 'askbowvue', actions: ['use'] },
  { module: 'activity', actions: ['read'] },
  { module: 'locations', actions: ['read', 'write', 'renewals'] },
  { module: 'users', actions: ['read', 'write', 'delete'] },
  { module: 'training', actions: ['read', 'write'] },
  { module: 'scheduling', actions: ['read', 'write'] },
  { module: 'support', actions: ['read', 'write'] },
]

// Which sites a user's permissions reach: every site, or only those they are
// assigned.
export const SITE_SCOPE_ALL = 'ALL'
export const SITE_SCOPE_ASSIGNED = 'ASSIGNED'

export const ALL_PERMISSIONS = PERMISSION_CATALOG.flatMap(
  ({ module, actions }) => actions.map((action) => `${module}.${action}`)
)

const KNOWN = new Set(ALL_PERMISSIONS)

export function knownPermission(permission) {
  return KNOWN.has(permission)
}

// ADMIN and DEVELOPER hold every permission, whatever /me says.
function unrestricted(roles) {
  return hasAnyRole(roles, 'ADMIN', 'DEVELOPER')
}

// Whether a user (the /me payload: roles and permissions) may do this. An
// unknown permission is never held. A module's read is implied by any other
// action of it, as on the API; /me already includes those, this keeps a stored
// payload honest too. Always a boolean, so it can go straight into a v-if.
export function can(userData, permission) {
  if (!knownPermission(permission)) return false
  const roles = userData && userData.roles
  if (unrestricted(roles)) return true
  const sent = userData && userData.permissions
  const held = (Array.isArray(sent) ? sent : []).filter(knownPermission)
  if (held.includes(permission)) return true
  const [module, action] = permission.split('.')
  return action === 'read' && held.some((p) => p.startsWith(`${module}.`))
}

// Every permission the user holds, in catalog order.
export function permissionsOf(userData) {
  return ALL_PERMISSIONS.filter((permission) => can(userData, permission))
}

// ALL for ADMIN and DEVELOPER, otherwise what /me says, and ASSIGNED (the
// narrower one) when it says nothing.
export function siteScopeOf(userData) {
  if (unrestricted(userData && userData.roles)) return SITE_SCOPE_ALL
  return userData && userData.siteScope === SITE_SCOPE_ALL
    ? SITE_SCOPE_ALL
    : SITE_SCOPE_ASSIGNED
}
